import React, { lazy, Suspense, useState, useEffect } from "react";
import { Navigate, Routes, Route, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SparkleBackground from "./components/SparkleBackground";
import SpecialHeader from "./components/SpecialHeader";
import ScrollProgressBar from "./components/ScrollProgressBar";
import CustomCursor from "./components/CustomCursor";
import LiveActivityIndicator from "./components/LiveActivityIndicator";
import AudioPlayer from "./components/AudioPlayer";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import MobileBottomNav from "./components/MobileBottomNav";
import { AdminProvider } from "./context/AdminContext";
import { useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toast";
import { NetworkStatusIndicator } from "./components/AsyncStates";
import { Analytics } from "./utils/analytics";

// Internal AI app runner route
const AIAppRunnerPage = lazy(() => import("./pages/AIAppRunnerPage"));

// TEMP DIAGNOSTIC: retry-aware dynamic import logger (revert after root cause confirmed)
// Helps distinguish "server already dead before import starts" vs "transform fails mid-request"
function debugDynamicImport<T>(
  importFn: () => Promise<{ default: T }>,
  name: string,
): () => Promise<{ default: T }> {
  return async () => {
    const t0 = Date.now();
    const ts = new Date().toISOString();
    console.log(`[DYNAMIC-IMPORT-DIAG] START ${name} @ ${ts}`);
    try {
      const mod = await importFn();
      console.log(`[DYNAMIC-IMPORT-DIAG] SUCCESS ${name} in ${Date.now() - t0}ms`);
      return mod;
    } catch (err: any) {
      console.error(`[DYNAMIC-IMPORT-DIAG] FAILED ${name} after ${Date.now() - t0}ms:`, {
        message: err?.message,
        stack: err?.stack?.split('\n').slice(0, 3).join('\n'),
      });
      throw err;
    }
  };
}

// Lazy loaded components for better performance
const LandingPage = lazy(debugDynamicImport(() => import("./components/premium/LandingPage"), "LandingPage"));
const AppPage = lazy(() => import("./pages/AppPage"));

// Generic pages
const PricingPage = lazy(() => import("./pages/PricingPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));

// SapienX AgentOS — loaded on demand so the workforce/runtime code never
// bloats the dashboard bundle.
const AgentOSMissionControlPage = lazy(() => import("./pages/agentos/MissionControlPage"));
const AgentOSWorkforcePage = lazy(() => import("./pages/agentos/WorkforcePage"));
const AgentOSWorkerNewPage = lazy(() => import("./pages/agentos/WorkerNewPage"));
const AgentOSWorkerDetailPage = lazy(() => import("./pages/agentos/WorkerDetailPage"));
const AgentOSWorkspacesPage = lazy(() => import("./pages/agentos/WorkspacesPage"));
const AgentOSWorkspaceDetailPage = lazy(() => import("./pages/agentos/WorkspaceDetailPage"));
const AgentOSMissionsPage = lazy(() => import("./pages/agentos/MissionsPage"));
const AgentOSMissionNewPage = lazy(() => import("./pages/agentos/MissionNewPage"));
const AgentOSMissionDetailPage = lazy(() => import("./pages/agentos/MissionDetailPage"));
const AgentOSSchedulesPage = lazy(() => import("./pages/agentos/SchedulesPage"));
const AgentOSApprovalsPage = lazy(() => import("./pages/agentos/ApprovalsPage"));
const AgentOSOperationsPage = lazy(() => import("./pages/agentos/OperationsPage"));
const AgentOSModelsPage = lazy(() => import("./pages/agentos/ModelsPage"));
const AgentOSConnectionsPage = lazy(() => import("./pages/agentos/ConnectionsPage"));
const AgentOSMemoryPage = lazy(() => import("./pages/agentos/MemoryPage"));
const AgentOSArtifactsPage = lazy(() => import("./pages/agentos/ArtifactsPage"));
const AgentOSActivityPage = lazy(() => import("./pages/agentos/ActivityPage"));
const AgentOSLayout = lazy(() => import("./pages/agentos/AgentOSLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminSignUp = lazy(() => import("./components/admin/AdminSignUp"));
const SpecialFooter = lazy(() => import("./components/SpecialFooter"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const AnalyticsDashboard = lazy(() => import("./components/AnalyticsDashboard"));

const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const EmailGTMPage = lazy(() => import("./pages/agents/EmailGTMPage"));
const FinancialCoachPage = lazy(() => import("./pages/agents/FinancialCoachPage"));
const ProductLaunchIntelligencePage = lazy(() => import("./pages/agents/ProductLaunchIntelligencePage"));
const LocalAiReasoningAgentPyPage = lazy(() => import("./pages/agents/LocalAiReasoningAgentPyPage"));
const LocalAiScrapperPyPage = lazy(() => import("./pages/agents/LocalAiScrapperPyPage"));
const LocalTravelAgentPage = lazy(() => import("./pages/agents/LocalTravelAgentPage"));
const SemanticSearchRagPage = lazy(() => import("./pages/agents/SemanticSearchRagPage"));
const RagAgentPage = lazy(() => import("./pages/agents/RagAgentPage"));
const ApplicationsPage = lazy(() => import("./pages/ApplicationsPage"));
const BirthdaySpecialPage = lazy(() => import("./pages/BirthdaySpecialPage"));

// Additional agent pages lazy imports
const Ai3dpygameR1Page = lazy(() => import("./pages/agents/Ai3dpygameR1Page"));
const AiAqiAnalysisAgentPage = lazy(() => import("./pages/agents/AiAqiAnalysisAgentPage"));
const AiArxivAgentMemoryPage = lazy(() => import("./pages/agents/AiArxivAgentMemoryPage"));
const AiAudioTourAgentPage = lazy(() => import("./pages/agents/AiAudioTourAgentPage"));
const AiBlogSearchPage = lazy(() => import("./pages/agents/AiBlogSearchPage"));
const AiBlogToPodcastAgentPage = lazy(() => import("./pages/agents/AiBlogToPodcastAgentPage"));
const AiBreakupRecoveryAgentPage = lazy(() => import("./pages/agents/AiBreakupRecoveryAgentPage"));
const AiChessAgentPage = lazy(() => import("./pages/agents/AiChessAgentPage"));
const AiCompetitorIntelligenceAgentTeamPage = lazy(() => import("./pages/agents/AiCompetitorIntelligenceAgentTeamPage"));
const AiCustomerSupportAgentPage = lazy(() => import("./pages/agents/AiCustomerSupportAgentPage"));
const AiDataAnalysisAgentPage = lazy(() => import("./pages/agents/AiDataAnalysisAgentPage"));
const AiDataVisualisationAgentPage = lazy(() => import("./pages/agents/AiDataVisualisationAgentPage"));
const AiDeepResearchAgentPage = lazy(() => import("./pages/agents/AiDeepResearchAgentPage"));
const AiDomainDeepResearchAgentPage = lazy(() => import("./pages/agents/AiDomainDeepResearchAgentPage"));
const AiEmailGtmOutreachAgentPage = lazy(() => import("./pages/agents/AiEmailGtmOutreachAgentPage"));
const AiEmailGtmReachoutAgentPage = lazy(() => import("./pages/agents/AiEmailGtmReachoutAgentPage"));
const AiFinancialCoachAgentPage = lazy(() => import("./pages/agents/AiFinancialCoachAgentPage"));
const AiFraudInvestigationAgentPage = lazy(() => import("./pages/agents/AiFraudInvestigationAgentPage"));
const AiGameDesignAgentTeamPage = lazy(() => import("./pages/agents/AiGameDesignAgentTeamPage"));
const AiHealthFitnessAgentPage = lazy(() => import("./pages/agents/AiHealthFitnessAgentPage"));
const AiJournalistAgentPage = lazy(() => import("./pages/agents/AiJournalistAgentPage"));
const AiLegalAgentTeamPage = lazy(() => import("./pages/agents/AiLegalAgentTeamPage"));
const AiLifeInsuranceAdvisorAgentPage = lazy(() => import("./pages/agents/AiLifeInsuranceAdvisorAgentPage"));
const AiMedicalImagingAgentPage = lazy(() => import("./pages/agents/AiMedicalImagingAgentPage"));
const AiMeetingAgentPage = lazy(() => import("./pages/agents/AiMeetingAgentPage"));
const AiMemeGeneratorAgentBrowserusePage = lazy(() => import("./pages/agents/AiMemeGeneratorAgentBrowserusePage"));
const AiMentalWellbeingAgentPage = lazy(() => import("./pages/agents/AiMentalWellbeingAgentPage"));
const AiMovieProductionAgentPage = lazy(() => import("./pages/agents/AiMovieProductionAgentPage"));
const AiMusicGeneratorAgentPage = lazy(() => import("./pages/agents/AiMusicGeneratorAgentPage"));
const AiPersonalFinanceAgentPage = lazy(() => import("./pages/agents/AiPersonalFinanceAgentPage"));
const ProductLaunchIntelligenceAgentPage = lazy(() => import("./pages/agents/ProductLaunchIntelligenceAgentPage"));
const AiRealEstateAgentTeamPage = lazy(() => import("./pages/agents/AiRealEstateAgentTeamPage"));
const AiReasoningAgentPage = lazy(() => import("./pages/agents/AiReasoningAgentPage"));
const AiRecipeMealPlanningAgentPage = lazy(() => import("./pages/agents/AiRecipeMealPlanningAgentPage"));
const AiRecruitmentAgentTeamPage = lazy(() => import("./pages/agents/AiRecruitmentAgentTeamPage"));
const AiServicesAgencyPage = lazy(() => import("./pages/agents/AiServicesAgencyPage"));
const AiStartupInsightFire1AgentPage = lazy(() => import("./pages/agents/AiStartupInsightFire1AgentPage"));
const AiStartupTrendAnalysisAgentPage = lazy(() => import("./pages/agents/AiStartupTrendAnalysisAgentPage"));
const AiSystemArchitectR1Page = lazy(() => import("./pages/agents/AiSystemArchitectR1Page"));
const AiTeachingAgentTeamPage = lazy(() => import("./pages/agents/AiTeachingAgentTeamPage"));
const AiTicTacToeAgentPage = lazy(() => import("./pages/agents/AiTicTacToeAgentPage"));
const AiTravelAgentMemoryPage = lazy(() => import("./pages/agents/AiTravelAgentMemoryPage"));
const AiTravelAgentPage = lazy(() => import("./pages/agents/AiTravelAgentPage"));
const AiTravelPlannerMcpAgentTeamPage = lazy(() => import("./pages/agents/AiTravelPlannerMcpAgentTeamPage"));

const AiConsultantAgentPage = lazy(() => import("./pages/agents/AiConsultantAgentPage"));
const AiInvestmentAgentPage = lazy(() => import("./pages/agents/AiInvestmentAgentPage"));
const AiSalesIntelligenceAgentTeamPage = lazy(() => import("./pages/agents/AiSalesIntelligenceAgentTeamPage"));
const AiVcDueDiligenceAgentTeamPage = lazy(() => import("./pages/agents/AiVcDueDiligenceAgentTeamPage"));
const EmailDrafterPage = lazy(() => import("./pages/agents/EmailDrafterPage"));
const StrategyAdvisorPage = lazy(() => import("./pages/agents/StrategyAdvisorPage"));
const ProjectPlannerPage = lazy(() => import("./pages/agents/ProjectPlannerPage"));
const MeetingNotesPage = lazy(() => import("./pages/agents/MeetingNotesPage"));
const DecisionHelperPage = lazy(() => import("./pages/agents/DecisionHelperPage"));
const FactCheckerPage = lazy(() => import("./pages/agents/FactCheckerPage"));
const AiHomeRenovationAgentPage = lazy(() => import("./pages/agents/AiHomeRenovationAgentPage"));
const AiNewsAndPodcastAgentsPage = lazy(() => import("./pages/agents/AiNewsAndPodcastAgentsPage"));
const MultimodalUiuxFeedbackAgentTeamPage = lazy(() => import("./pages/agents/MultimodalUiuxFeedbackAgentTeamPage"));
const ContentCreatorPage = lazy(() => import("./pages/agents/ContentCreatorPage"));
const EditorPage = lazy(() => import("./pages/agents/EditorPage"));
const UxDesignerPage = lazy(() => import("./pages/agents/UxDesignerPage"));
const VisualizationExpertPage = lazy(() => import("./pages/agents/VisualizationExpertPage"));
const AiTravelPlannerAgentTeamPage = lazy(() => import("./pages/agents/AiTravelPlannerAgentTeamPage"));
const AiSelfEvolvingAgentPage = lazy(() => import("./pages/agents/AiSelfEvolvingAgentPage"));
const NotionMcpAgentPage = lazy(() => import("./pages/agents/NotionMcpAgentPage"));
const RagFailureDiagnosticsClinicPage = lazy(() => import("./pages/agents/RagFailureDiagnosticsClinicPage"));
const SelfImprovingAgentSkillsPage = lazy(() => import("./pages/agents/SelfImprovingAgentSkillsPage"));
const AcademicResearcherPage = lazy(() => import("./pages/agents/AcademicResearcherPage"));
const CodeReviewerPage = lazy(() => import("./pages/agents/CodeReviewerPage"));
const DataAnalystPage = lazy(() => import("./pages/agents/DataAnalystPage"));
const AgentAppPage = lazy(() => import("./pages/agents/AppPage"));
const AutonomousRagPage = lazy(() => import("./pages/agents/AutonomousRagPage"));
const PersonalizerPage = lazy(() => import("./pages/PersonalizerPage"));
const BlogToPodcastAgentPage = lazy(() => import("./pages/agents/BlogToPodcastAgentPage"));
const BrowserMcpAgentPage = lazy(() => import("./pages/agents/BrowserMcpAgentPage"));
const ChatWithGithubPage = lazy(() => import("./pages/agents/ChatWithGithubPage"));
const ChatWithGmailPage = lazy(() => import("./pages/agents/ChatWithGmailPage"));
const ChatWithPdfPage = lazy(() => import("./pages/agents/ChatWithPdfPage"));
const ChatWithResearchPapersPage = lazy(() => import("./pages/agents/ChatWithResearchPapersPage"));
const ChatWithSubstackPage = lazy(() => import("./pages/agents/ChatWithSubstackPage"));
const ChatWithTarotsPage = lazy(() => import("./pages/agents/ChatWithTarotsPage"));
const ChatWithYoutubeVideosPage = lazy(() => import("./pages/agents/ChatWithYoutubeVideosPage"));
const ContextualaiRagAgentPage = lazy(() => import("./pages/agents/ContextualaiRagAgentPage"));
const CorrectiveRagPage = lazy(() => import("./pages/agents/CorrectiveRagPage"));
const CursorAiExperimentsPage = lazy(() => import("./pages/agents/CursorAiExperimentsPage"));
const CustomerSupportVoiceAgentPage = lazy(() => import("./pages/agents/CustomerSupportVoiceAgentPage"));
const DocumentRetrievalAgentPage = lazy(() => import("./pages/agents/DocumentRetrievalAgentPage"));
const DevpulseAiPage = lazy(() => import("./pages/agents/DevpulseAiPage"));
const FinanceAgentPage = lazy(() => import("./pages/agents/FinanceAgentPage"));
const FrontendPage = lazy(() => import("./pages/agents/FrontendPage"));
const AgenticRagPage = lazy(() => import("./pages/agents/AgenticRagPage"));
const GithubMcpAgentPage = lazy(() => import("./pages/agents/GithubMcpAgentPage"));
const GptOssCritiqueImprovementLoopPage = lazy(() => import("./pages/agents/GptOssCritiqueImprovementLoopPage"));
const HybridSearchRagPage = lazy(() => import("./pages/agents/HybridSearchRagPage"));
const KnowledgeGraphRagCitationsPage = lazy(() => import("./pages/agents/KnowledgeGraphRagCitationsPage"));
const LocalRagPage = lazy(() => import("./pages/agents/LocalRagPage"));
const StatefulChatPage = lazy(() => import("./pages/agents/StatefulChatPage"));
const AiPersonalizedMemoryPage = lazy(() => import("./pages/agents/AiPersonalizedMemoryPage"));
const AiRouterAppPage = lazy(() => import("./pages/agents/AiRouterAppPage"));
const LocalAiLegalAgentTeamPage = lazy(() => import("./pages/agents/LocalAiLegalAgentTeamPage"));
const LocalChatgptClonePage = lazy(() => import("./pages/agents/LocalChatgptClonePage"));
const LocalChatgptWithMemoryPage = lazy(() => import("./pages/agents/LocalChatgptWithMemoryPage"));
const LocalHybridSearchRagPage = lazy(() => import("./pages/agents/LocalHybridSearchRagPage"));
const MixtureOfAgentsPage = lazy(() => import("./pages/agents/MixtureOfAgentsPage"));
const MultiAgentResearcherPage = lazy(() => import("./pages/agents/MultiAgentResearcherPage"));
const MultiAiMemoryPage = lazy(() => import("./pages/agents/MultiAiMemoryPage"));
const MultiMcpAgentRouterPage = lazy(() => import("./pages/agents/MultiMcpAgentRouterPage"));
const MultimodalAiAgentPage = lazy(() => import("./pages/agents/MultimodalAiAgentPage"));
const MultimodalCodingAgentTeamPage = lazy(() => import("./pages/agents/MultimodalCodingAgentTeamPage"));
const MultimodalDesignAgentTeamPage = lazy(() => import("./pages/agents/MultimodalDesignAgentTeamPage"));
const MusicGeneratorAgentPyPage = lazy(() => import("./pages/agents/MusicGeneratorAgentPyPage"));
const OpenaiResearchAgentPage = lazy(() => import("./pages/agents/OpenaiResearchAgentPage"));
const PodcastifyAIPage = lazy(() => import("./pages/agents/PodcastifyAIPage"));
const RagAsAServicePage = lazy(() => import("./pages/agents/RagAsAServicePage"));
const RagChainPage = lazy(() => import("./pages/agents/RagChainPage"));
const RagDatabaseRoutingPage = lazy(() => import("./pages/agents/RagDatabaseRoutingPage"));
const ReasoningAgentPage = lazy(() => import("./pages/agents/ReasoningAgentPage"));
const ResearchAgentPage = lazy(() => import("./pages/agents/ResearchAgentPage"));
const ResumeJobMatcherPage = lazy(() => import("./pages/agents/ResumeJobMatcherPage"));
const SalesForceAIPage = lazy(() => import("./pages/agents/SalesForceAIPage"));
const SocialBuzzAIPage = lazy(() => import("./pages/agents/SocialBuzzAIPage"));
const StartupTrendsAgentPage = lazy(() => import("./pages/agents/StartupTrendsAgentPage"));
const ToonifyTokenOptimizationPage = lazy(() => import("./pages/agents/ToonifyTokenOptimizationPage"));
const TrustGatedAgentTeamPage = lazy(() => import("./pages/agents/TrustGatedAgentTeamPage"));
const VisionRagPage = lazy(() => import("./pages/agents/VisionRagPage"));
const VoiceRagOpenaisdkPage = lazy(() => import("./pages/agents/VoiceRagOpenaisdkPage"));
const WebScrapingAgentPage = lazy(() => import("./pages/agents/WebScrapingAgentPage"));
const WebScrapingAiAgentPage = lazy(() => import("./pages/agents/WebScrapingAiAgentPage"));
const XaiFinanceAgentPage = lazy(() => import("./pages/agents/XaiFinanceAgentPage"));
  const StarterAgentPage = lazy(() => import("./pages/agents/1StarterAgentPage"));
const RunningAgentsPage = lazy(() => import("./pages/agents/4RunningAgentsPage"));
const InMemoryConversationAgentPage = lazy(() => import("./pages/agents/51InMemoryConversationAgentPage"));
const PersistentConversationAgentPage = lazy(() => import("./pages/agents/52PersistentConversationAgentPage"));
const AgentLifecycleCallbacksPage = lazy(() => import("./pages/agents/61AgentLifecycleCallbacksPage"));
const AiInteractionCallbacksPage = lazy(() => import("./pages/agents/62AiInteractionCallbacksPage"));
const ToolExecutionCallbacksPage = lazy(() => import("./pages/agents/63ToolExecutionCallbacksPage"));
const PluginsPage = lazy(() => import("./pages/agents/7PluginsPage"));
const SessionsPage = lazy(() => import("./pages/agents/7SessionsPage"));
const SequentialAgentPage = lazy(() => import("./pages/agents/91SequentialAgentPage"));
const LoopAgentPage = lazy(() => import("./pages/agents/92LoopAgentPage"));
const ParallelAgentPage = lazy(() => import("./pages/agents/93ParallelAgentPage"));
const Ag2AdaptiveResearchTeamPage = lazy(() => import("./pages/agents/Ag2AdaptiveResearchTeamPage"));
const AgenticRagEmbeddingGemmaPage = lazy(() => import("./pages/agents/AgenticRagEmbeddingGemmaPage"));
const AgenticRagGpt5Page = lazy(() => import("./pages/agents/AgenticRagGpt5Page"));
const AgenticRagWithReasoningPage = lazy(() => import("./pages/agents/AgenticRagWithReasoningPage"));
const ConsultProAIPage = lazy(() => import("./pages/agents/ConsultProAIPage"));
const LaunchRocketAIPage = lazy(() => import("./pages/agents/LaunchRocketAIPage"));
// New apps using generic AppPage
const AiHeadshotStudioPage = AgentAppPage;
const AiRoyalPortraitPage = AgentAppPage;
const AiAgeTransformationPage = AgentAppPage;
const AiTryOnPage = AgentAppPage;
const AiProfessionalMakeupGeneratorPage = AgentAppPage;
const AiHairStyleSimulatorPage = AgentAppPage;
const AiKidsToAdultPredictionPage = AgentAppPage;
const AiRoomDeclutterPage = AgentAppPage;
const AiFitnessBodySimulatorPage = AgentAppPage;
const AiPetPortraitPage = AgentAppPage;
const AiWeddingPhotoPage = AgentAppPage;
const AiTattooTryOnPage = AgentAppPage;
const AiLogoPage = AgentAppPage;
const OldPhotoPage = AgentAppPage;
const NanoBananaStudioPage = AgentAppPage;
const SeedanceV2StudioPage = AgentAppPage;
const EasyVeoPage = AgentAppPage;
const AiClipPage = AgentAppPage;
const VideoCreatorPage = AgentAppPage;
const TextToSpeechPage = AgentAppPage;
const NicheScriptPage = AgentAppPage;
const PromoGeneratorPage = AgentAppPage;
const LandingPageAgentPage = AgentAppPage;
const AiBusinessCardPage = AgentAppPage;
const MailwisePage = AgentAppPage;
const MyPodcastPage = AgentAppPage;
const EZScribePage = AgentAppPage;
const TalkToPdfPage = AgentAppPage;
const AiKnowledgeBasePage = AgentAppPage;
const AiOutboundPage = AgentAppPage;
const SalesMonetizerPage = AgentAppPage;
const RebranderAiPage = AgentAppPage;
const BgRemoverPage = AgentAppPage;
const AiArtPage = AgentAppPage;
const SocialPackPage = AgentAppPage;
const SmartPresentationsPage = AgentAppPage;
const InteractiveOutrosPage = AgentAppPage;
const InteractiveShoppingPage = AgentAppPage;
const StoryboardPage = AgentAppPage;
const PersonalizerVideoImageTransformerPage = AgentAppPage;
const AiTemplateGeneratorPage = AgentAppPage;
const AiSignaturePage = AgentAppPage;
const ThumbnailGeneratorPage = AgentAppPage;
const VoiceCoachPage = AgentAppPage;
const AmazonProductStudioPage = AgentAppPage;
const ResalePhotoEnhancerPage = AgentAppPage;
const PetProductStudioPage = AgentAppPage;
const AiRecruiterPage = AgentAppPage;
const BloggerCmsPage = AgentAppPage;
const AiRealEstateStagerPage = AgentAppPage;
const PromptArchitectPage = AgentAppPage;
const ClearmarkAiPage = AgentAppPage;
const AiFlashCardsPage = AgentAppPage;
const AiGroupPhotoPage = AgentAppPage;
const AiCharacterStudioPage = AgentAppPage;
const LuxuryHairStudioPage = AgentAppPage;
const AiTravelStudioPage = AgentAppPage;
const SocialPostPage = AgentAppPage;
const PlantVisionAiPage = AgentAppPage;
const AiResumeBuilderPage = AgentAppPage;
const GeoCheckerPage = AgentAppPage;
const SolaceAiPage = AgentAppPage;
const ReLiveAiPage = AgentAppPage;
const AiChiropracticServicePage = AgentAppPage;
const VeloraYogaAiPage = AgentAppPage;
const AiKissingVideoGeneratorPage = AgentAppPage;
const TablaReserveAiPage = AgentAppPage;
const DentalReserveAiPage = AgentAppPage;
const CounselMatePage = AgentAppPage;
const FixeraPage = AgentAppPage;
const VertexTaxStrategyPage = AgentAppPage;
const LedgerSyncPage = AgentAppPage;
const ProFlowPlumbingPage = AgentAppPage;
const TurboGlowAutoSpaPage = AgentAppPage;
const PawsPalsPage = AgentAppPage;
const TowMatePage = AgentAppPage;
const SwiftLinkLogisticsPage = AgentAppPage;
const LumeaResidencePage = AgentAppPage;
const OpulentDrivePage = AgentAppPage;
const ProFixAutoPage = AgentAppPage;
const NovaAssuranceAiPage = AgentAppPage;
const NovaCareClinicPage = AgentAppPage;

// Auth pages
const SignInPage = lazy(() => import("./pages/SignInPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailConfirmPage = lazy(() => import("./pages/EmailConfirmPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const MagicLinkPage = lazy(() => import("./pages/MagicLinkPage"));

// Agent components map for dynamic routing
const agentComponents = {
  'ai-news-content-writer': StarterAgentPage,
  'ai-video-script-producer': RunningAgentsPage,
  'ai-music-jingle-assistant': InMemoryConversationAgentPage,
  'ai-film-producer': PersistentConversationAgentPage,
  'podcast-creator-ai': AgentLifecycleCallbacksPage,
   'news-to-podcast-ai': AiInteractionCallbacksPage,
  'ai-voice-support-agent': ToolExecutionCallbacksPage,
'talk-to-your-business-ai': PluginsPage,
   'ai-audio-guide-creator': SessionsPage,
   'ai-intake-voice-agent': SequentialAgentPage,
   'ai-dictation-assistant': LoopAgentPage,
  'business-knowledgebase-ai': ParallelAgentPage,
  'pdf-business-assistant': Ag2AdaptiveResearchTeamPage,
  'research-paper-assistant': AgenticRagEmbeddingGemmaPage,
  'codebase-chat-ai': AgenticRagGpt5Page,
  'gmail-intelligence-ai': AgenticRagWithReasoningPage,
  'video-knowledge-assistant': Ai3dpygameR1Page,
  'blog-knowledge-search-ai': AiAqiAnalysisAgentPage,
'research-memory-assistant': AiArxivAgentMemoryPage,
   'ai-dashboard-designer-ai': AiBlogToPodcastAgentPage,
  'multimodal-knowledge-ai': AiBreakupRecoveryAgentPage,
  'ai-knowledgebase-debugger': AiChessAgentPage,
  'competitor-spy-ai': AiCompetitorIntelligenceAgentTeamPage,
  'home-renovation-visualizer-ai': AiCustomerSupportAgentPage,
  'local-business-analytics-ai': AiDataAnalysisAgentPage,
  'local-tour-guide-ai': AiDataVisualisationAgentPage,
  'deep-research-pro': AiDeepResearchAgentPage,
  'local-business-growth-advisor': AiDomainDeepResearchAgentPage,
'candidate-outreach-ai': AiEmailGtmOutreachAgentPage,
   'profit-coach-ai': FinancialCoachPage,
  'fraud-investigation-assistant': AiFraudInvestigationAgentPage,
  'interview-summary-ai': AiGameDesignAgentTeamPage,
'hiring-plan-builder': AiHealthFitnessAgentPage,
'contract-summary-ai': AiLegalAgentTeamPage,
   'investment-research-assistant': AiMedicalImagingAgentPage,
  'startup-due-diligence-ai': AiMeetingAgentPage,
  'revenue-data-analyst-ai': AiMemeGeneratorAgentBrowserusePage,
'financial-dashboard-ai': AiMentalWellbeingAgentPage,
   'policy-compliance-assistant': AiPersonalFinanceAgentPage,
  'launch-campaign-builder-ai': ProductLaunchIntelligencePage,
'real-estate-marketing-ai': AiRealEstateAgentTeamPage,
   'ai-hiring-assistant': AiRecruitmentAgentTeamPage,
  'ai-agency-builder-suite': AiServicesAgencyPage,
  'ai-code-review-pro': AiStartupInsightFire1AgentPage,
  'ai-bug-fixer': AiStartupTrendAnalysisAgentPage,
  'ai-fullstack-builder': AiSystemArchitectR1Page,
  'ai-course-creator-assistant': AiTeachingAgentTeamPage,
  'github-repo-assistant': AiTicTacToeAgentPage,
'ai-consultant-agent': AiConsultantAgentPage,
'ai-investment-agent': AiInvestmentAgentPage,
'ai-sales-intelligence-agent-team': AiSalesIntelligenceAgentTeamPage,
'ai-vc-due-diligence-agent-team': AiVcDueDiligenceAgentTeamPage,
'email-drafter': EmailDrafterPage,
'strategy-advisor': StrategyAdvisorPage,
'project-planner': ProjectPlannerPage,
'meeting-notes': MeetingNotesPage,
'decision-helper': DecisionHelperPage,
'fact-checker': FactCheckerPage,
'ai-home-renovation-agent': AiHomeRenovationAgentPage,
'ai-news-and-podcast-agents': AiNewsAndPodcastAgentsPage,
'multimodal-uiux-feedback-agent-team': MultimodalUiuxFeedbackAgentTeamPage,
'content-creator': ContentCreatorPage,
'editor': EditorPage,
'ux-designer': UxDesignerPage,
'visualization-expert': VisualizationExpertPage,
'ai-travel-planner-agent-team': AiTravelPlannerAgentTeamPage,
'ai-self-evolving-agent': AiSelfEvolvingAgentPage,
'notion-mcp-agent': NotionMcpAgentPage,
'rag-failure-diagnostics-clinic': RagFailureDiagnosticsClinicPage,
'self-improving-agent-skills': SelfImprovingAgentSkillsPage,
'academic-researcher': AcademicResearcherPage,
'code-reviewer': CodeReviewerPage,
'data-analyst': DataAnalystPage,
  'landing-page-critic-ai': AutonomousRagPage,

   'newsletter-repurposer-ai': ChatWithSubstackPage,
'academic-research-ai': ChatWithTarotsPage,
   'market-research-ai': ContextualaiRagAgentPage,
  'fact-check-ai': CorrectiveRagPage,
  'local-business-voice-assistant': CustomerSupportVoiceAgentPage,
'multi-ai-memory-hub': DocumentRetrievalAgentPage,
   'private-chatgpt-clone': FrontendPage,
  'gemini-agentic-rag': AgenticRagPage,
  'gpt-oss-critique-improvement-loop': GptOssCritiqueImprovementLoopPage,
  'smart-search-ai': HybridSearchRagPage,
  'knowledge-graph-rag-citations': KnowledgeGraphRagCitationsPage,
'llama3-1-local-rag': LocalRagPage,
'llama3-stateful-chat': StatefulChatPage,
  'ai-personalized-memory': AiPersonalizedMemoryPage,
  'ai-router-ai-design-studio': AiRouterAppPage,
  'local-contract-summary-ai': LocalAiLegalAgentTeamPage,
  'local-risk-decision-ai-py': LocalAiReasoningAgentPyPage,
  'local-ai-scrapper-py': LocalAiScrapperPyPage,
  'local-chatgpt-clone': LocalChatgptClonePage,
  'local-chatgpt-with-memory': LocalChatgptWithMemoryPage,
  'local-smart-search-ai': LocalHybridSearchRagPage,
  'local-travel-agent': LocalTravelAgentPage,
  'mixture-of-agents': MixtureOfAgentsPage,
  'multi-agent-researcher': MultiAgentResearcherPage,
  'multi-ai-memory': MultiAiMemoryPage,
  'multi-mcp-agent-router': MultiMcpAgentRouterPage,
  'multimodal-ai-agent': MultimodalAiAgentPage,
'ai-app-builder-assistant': MultimodalCodingAgentTeamPage,
   'music-generator-agent-py': MusicGeneratorAgentPyPage,
  'research-assistant-ai': OpenaiResearchAgentPage,
  'podcastify-ai': PodcastifyAIPage,
  'qwen-local-rag': SemanticSearchRagPage,
'rag-agent-cohere': RagAgentPage,
   'rag-chain': RagChainPage,
  'rag-database-routing': RagDatabaseRoutingPage,
  'reasoning-agent': ReasoningAgentPage,
  'research-agent-gemini-interaction-api': ResearchAgentPage,
  'resume-job-matcher': ResumeJobMatcherPage,
  'startup-trends-agent': StartupTrendsAgentPage,
  'toonify-token-optimization': ToonifyTokenOptimizationPage,
  'trust-gated-agent-team': TrustGatedAgentTeamPage,
  'vision-rag': VisionRagPage,
  'voice-rag-openaisdk': VoiceRagOpenaisdkPage,
  'lead-research-scraper-ai': WebScrapingAiAgentPage,
  'finance-research-ai': XaiFinanceAgentPage,
  // New apps with dedicated pages
  'ai-headshot-studio': AiHeadshotStudioPage,
  'ai-royal-portrait': AiRoyalPortraitPage,
  'ai-age-transformation': AiAgeTransformationPage,
  'ai-try-on': AiTryOnPage,
  'ai-professional-makeup-generator': AiProfessionalMakeupGeneratorPage,
  'ai-hair-style-simulator': AiHairStyleSimulatorPage,
  'ai-kids-to-adult-prediction': AiKidsToAdultPredictionPage,
  'ai-room-declutter': AiRoomDeclutterPage,
  'ai-fitness-body-simulator': AiFitnessBodySimulatorPage,
  'ai-pet-portrait': AiPetPortraitPage,
  'ai-wedding-photo': AiWeddingPhotoPage,
  'ai-tattoo-try-on': AiTattooTryOnPage,
  'ai-logo': AiLogoPage,
  'old-photo': OldPhotoPage,
  'nano-banana-studio': NanoBananaStudioPage,
  'seedance-v2-studio': SeedanceV2StudioPage,
  'easyveo': EasyVeoPage,
  'aiclip': AiClipPage,
  'video-creator': VideoCreatorPage,
  'text-to-speech': TextToSpeechPage,
  'niche-script': NicheScriptPage,
  'promo-generator': PromoGeneratorPage,
  'landing-page': LandingPageAgentPage,
  'ai-business-card': AiBusinessCardPage,
  'mailwise': MailwisePage,
  'my-podcast': MyPodcastPage,
  'ezscribe': EZScribePage,
  'talk-to-pdf': TalkToPdfPage,
  'ai-knowledge-base': AiKnowledgeBasePage,
  'ai-outbound': AiOutboundPage,
'sales-monetizer': AgentAppPage,
   'rebrander-ai': RebranderAiPage,
  'bg-remover': BgRemoverPage,
  'ai-art': AiArtPage,
  'social-pack': SocialPackPage,
  'smart-presentations': SmartPresentationsPage,
  'interactive-outros': InteractiveOutrosPage,
  'interactive-shopping': InteractiveShoppingPage,
  'storyboard': StoryboardPage,
  'youtube-repurposer-ai': PersonalizerVideoImageTransformerPage,
  'ai-content-editor': AiTemplateGeneratorPage,
  'ai-content-creator-pro': AiSignaturePage,
  'thumbnail-generator': ThumbnailGeneratorPage,
  'voice-coach': VoiceCoachPage,
  'amazon-product-studio': AmazonProductStudioPage,
  'resale-photo-enhancer': ResalePhotoEnhancerPage,
  'pet-product-studio': PetProductStudioPage,
  'ai-recruiter': AiRecruiterPage,
  'blogger-cms': BloggerCmsPage,
  'ai-real-estate-stager': AiRealEstateStagerPage,
  'prompt-architect': PromptArchitectPage,
  'clearmark-ai': ClearmarkAiPage,
  'ai-flash-cards': AiFlashCardsPage,
  'ai-group-photo': AiGroupPhotoPage,
  'ai-character-studio': AiCharacterStudioPage,
  'luxury-hair-studio': LuxuryHairStudioPage,
  'ai-travel-studio': AiTravelStudioPage,
  'social-post': SocialPostPage,
  'plantvision-ai': PlantVisionAiPage,
  'ai-resume-builder': AiResumeBuilderPage,
  'geo-checker': GeoCheckerPage,
  'solace-ai': SolaceAiPage,
  'relive-ai': ReLiveAiPage,
  'ai-chiropractic-service': AiChiropracticServicePage,
  'velora-yoga-ai': VeloraYogaAiPage,
  'ai-kissing-video-generator': AiKissingVideoGeneratorPage,
  'tabla-reserveai': TablaReserveAiPage,
  'dental-reserveai': DentalReserveAiPage,
  'counselmate': CounselMatePage,
  'fixera': FixeraPage,
  'vertex-tax-strategy': VertexTaxStrategyPage,
  'ledgersync': LedgerSyncPage,
  'proflow-plumbing': ProFlowPlumbingPage,
  'turboglow-auto-spa': TurboGlowAutoSpaPage,
  'paws-pals': PawsPalsPage,
  'towmate': TowMatePage,
  'swiftlink-logistics': SwiftLinkLogisticsPage,
  'lumea-residence': LumeaResidencePage,
  'opulent-drive': OpulentDrivePage,
  'profix-auto': ProFixAutoPage,
  'nova-assuranceai': NovaAssuranceAiPage,
  'nova-care-clinic': NovaCareClinicPage,
};

// Agent page component for dynamic routing
const AgentPage = () => {
  const { id } = useParams();
  const Component = agentComponents[id];
  if (!Component) {
    return <div>Agent page not found</div>;
  }
  return <Component />;
};

// Loading fallback component
const SectionLoader = () => (
  <div className="flex justify-center items-center py-20 text-white">
    <div className="relative">
      <div className="w-16 h-16 border-t-4 border-primary-500 border-solid rounded-full animate-spin"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs text-primary-500 font-medium">Loading</span>
      </div>
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const { user } = useAuth();

  // Detect mobile/tablet
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

// Check if we're on an admin page
   const isAdminPage = location.pathname.startsWith("/admin");

   // Check if we should show the global personalizer button
   // Only show for authenticated users, NOT on landing page, admin pages, or auth pages
   const isAuthPage = location.pathname.startsWith("/signin") ||
                      location.pathname.startsWith("/signup") ||
                      location.pathname.startsWith("/forgot-password") ||
                      location.pathname.startsWith("/reset-password") ||
                      location.pathname.startsWith("/email-confirm") ||
                      location.pathname.startsWith("/auth-callback") ||
                      location.pathname.startsWith("/magic-link");
   const isLandingPage = location.pathname === "/";
   const isBirthdaySpecialPage = location.pathname === "/birthday-special";
   const isDashboardPage =
     location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");
   const showGlobalPersonalizer =
     user &&
     !isAdminPage &&
     !isAuthPage &&
     !isLandingPage &&
     !isBirthdaySpecialPage &&
     !isDashboardPage;

   useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Initialize analytics with user ID
  useEffect(() => {
    Analytics.setUserId(user?.id || null);
  }, [user]);
  // Update document title with page section
  useEffect(() => {
    const updateTitle = () => {
      const sections = document.querySelectorAll("section[id]");
      let currentSection = "home";

      for (const section of sections as NodeListOf<HTMLElement>) {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (
          window.scrollY >= sectionTop - 200 &&
          window.scrollY < sectionTop + sectionHeight - 200
        ) {
          currentSection = section.id;
          break;
        }
      }

      document.title = `VideoRemix.vip | ${currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}`;
    };

    window.addEventListener("scroll", updateTitle);
    return () => window.removeEventListener("scroll", updateTitle);
  }, []);

// Handle errors from error boundaries
   const handleError = () => {
     // In a production ai-design-studio, you might send this to an error tracking service
   };

    return (
     <>
      <Helmet>
        <title>VideoRemix.vip - AI-Powered Marketing Personalization Platform</title>
        <meta name="description" content="Create personalized marketing content that converts with AI-powered tools. Transform your campaigns with VideoRemix.vip's marketing personalization platform." />
      </Helmet>
      <div className="flex flex-col min-h-screen bg-gray-900 text-white">

        {!isAdminPage && !isBirthdaySpecialPage && !isDashboardPage && <SpecialHeader topOffset={0} />}

        {!isAdminPage && !isBirthdaySpecialPage && !isDashboardPage && <ScrollProgressBar topOffset={0} />}

        {!isMobile && !isTablet && !isAdminPage && !isBirthdaySpecialPage && !isDashboardPage && <CustomCursor />}

        {!isAdminPage && !isBirthdaySpecialPage && !isDashboardPage && <AudioPlayer />}

        {!isAdminPage && !isBirthdaySpecialPage && !isDashboardPage && <LiveActivityIndicator />}

        {/* Global Personalizer Button - only for authenticated users, not on landing/auth pages */}
        {!isAdminPage && showGlobalPersonalizer && (
          <Suspense fallback={null}>
            <GlobalPersonalizerButton />
          </Suspense>
        )}

        <Routes>
          {/* Landing Page Route */}
          <Route
            path="/"
            element={
              <ErrorBoundary onError={handleError}>
                <LandingPage />
              </ErrorBoundary>
            }
          />

          {/* Dean's 46th Birthday Software Special */}
          <Route
            path="/birthday-special"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <BirthdaySpecialPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Courses Page */}
          <Route
            path="/courses"
            element={
              <ErrorBoundary onError={handleError}>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <CoursesPage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ErrorBoundary>
            }
          />

          {/* Pricing Page */}
          <Route
            path="/pricing"
            element={
              <ErrorBoundary onError={handleError}>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <PricingPage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ErrorBoundary>
            }
          />

          {/* FAQ Page */}
          <Route
            path="/faq"
            element={
              <ErrorBoundary onError={handleError}>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <FAQPage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ErrorBoundary>
            }
          />

          {/* Legacy catalog aliases now resolve to the canonical app library */}
          <Route path="/tools" element={<Navigate to="/apps" replace />} />
          <Route path="/applications" element={<Navigate to="/apps" replace />} />

          {/* Applications Page - All apps visible to everyone, locked/owned badges shown */}
          <Route
            path="/apps"
            element={
              <ErrorBoundary onError={handleError}>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <ApplicationsPage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ErrorBoundary>
            }
          />

          {/* Individual App Pages */}
          <Route
            path="/ai-design-studio/:appId"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <AppPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* AI App Runner - where users actually run the 100 internal AI apps */}
          <Route
            path="/ai-runner/:slug"
            element={
              <ProtectedRoute>
                <ErrorBoundary onError={handleError}>
                  <SparkleBackground>
                    <Suspense fallback={<SectionLoader />}>
                      <AIAppRunnerPage />
                    </Suspense>
                  </SparkleBackground>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* SapienX is the single authenticated VideoRemix dashboard shell */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary onError={handleError}>
                  <Suspense fallback={<SectionLoader />}>
                    <DashboardPage />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* SapienX AgentOS — digital workforce, workspaces, missions, runtime */}
          {/*
            The section has its own layout: the nav bar and the notification bell
            are rendered once here, and every page below renders inside it.
          */}
          <Route
            path="/agentos"
            element={
              <ProtectedRoute>
                <ErrorBoundary onError={handleError}>
                  <Suspense fallback={<SectionLoader />}>
                    <AgentOSLayout />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          >
          <Route
            index
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMissionControlPage />
              </Suspense>
            }
          />
          <Route
            path="mission-control"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMissionControlPage />
              </Suspense>
            }
          />
          <Route
            path="workforce"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSWorkforcePage />
              </Suspense>
            }
          />
          <Route
            path="workforce/new"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSWorkerNewPage />
              </Suspense>
            }
          />
          <Route
            path="workforce/:workerId"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSWorkerDetailPage />
              </Suspense>
            }
          />
          <Route
            path="workspaces"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSWorkspacesPage />
              </Suspense>
            }
          />
          <Route
            path="workspaces/:workspaceId"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSWorkspaceDetailPage />
              </Suspense>
            }
          />
          <Route
            path="missions"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMissionsPage />
              </Suspense>
            }
          />
          <Route
            path="missions/new"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMissionNewPage />
              </Suspense>
            }
          />
          <Route
            path="missions/:missionId"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMissionDetailPage />
              </Suspense>
            }
          />
          <Route
            path="schedules"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSSchedulesPage />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSApprovalsPage />
              </Suspense>
            }
          />
          <Route
            path="operations"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSOperationsPage />
              </Suspense>
            }
          />
          <Route
            path="models"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSModelsPage />
              </Suspense>
            }
          />
          <Route
            path="connections"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSConnectionsPage />
              </Suspense>
            }
          />
          <Route
            path="memory"
            element={
              <Suspense fallback={<SectionLoader />}>
                <AgentOSMemoryPage />
              </Suspense>
            }
          />
            <Route
              path="artifacts"
              element={
                <Suspense fallback={<SectionLoader />}>
                  <AgentOSArtifactsPage />
                </Suspense>
              }
            />
            <Route
              path="activity"
              element={
                <Suspense fallback={<SectionLoader />}>
                  <AgentOSActivityPage />
                </Suspense>
              }
            />
          </Route>

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <ProfilePage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SectionLoader />}>
                  <AnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <SparkleBackground>
                  <Suspense fallback={<SectionLoader />}>
                    <CreditsPage />
                    <SpecialFooter />
                  </Suspense>
                </SparkleBackground>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SectionLoader />}>
                  <SettingsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/personalizer"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SectionLoader />}>
                  <PersonalizerPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Agent Pages */}
          <Route
            path="/agents/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SectionLoader />}>
                  <AgentPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Auth pages */}
          <Route
            path="/signin/*"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <SignInPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/signup/*"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <SignUpPage />
                  <SpecialFooter />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <ForgotPasswordPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/reset-password"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <ResetPassword />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/email-confirm"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <EmailConfirmPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/auth/callback"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <AuthCallback />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="/magic-link"
            element={
              <ErrorBoundary onError={handleError}>
                <Suspense fallback={<SectionLoader />}>
                  <MagicLinkPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminProvider>
                <Suspense fallback={<SectionLoader />}>
                  <AdminDashboard />
                </Suspense>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/login"
            element={
              <AdminProvider>
                <Suspense fallback={<SectionLoader />}>
                  <AdminLogin />
                </Suspense>
              </AdminProvider>
            }
          />
        </Routes>
        <Toaster />
        {!isDashboardPage && <MobileBottomNav />}
        {!isDashboardPage && <NetworkStatusIndicator />}
      </div>
    </>
  );
}

export default App;
